import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { LuMail, LuLock, LuEye, LuEyeOff, LuArrowRight, LuTriangleAlert } from 'react-icons/lu';
import { COPY } from '../../contracts/copyMap';

// The auth form card on Sign-In / Sign-Up (design-recreation).
//
// Behaviour stays in the parent page (auth provider integration, redirects,
// confirm-password validation, password reset). This component only owns:
// field state, the eye toggle, and submit/click delegation.
//
// Props:
//   mode:           'sign-in' | 'sign-up'
//   onModeChange:   (mode) => void
//   onSubmit:       (mode, { email, password, confirmPassword }) => Promise
//   onGoogle:       () => Promise   (optional — hides button if absent)
//   onApple:        () => void      (optional — hides button if absent)
//   onForgot:       (email) => void (optional — hides link if absent; login only)
//   submitting:     bool
//   error:          null | string | { title, detail?, action? }
//                   rendered as the red alert banner above the fields. A string
//                   is treated as { title }. action = { type:'recover'|'login', label }.
//   notice:         string|null     non-error inline message (aurum)
//   onOpenLegal:    (doc) => void   opens the shared legal modal; doc is
//                                   'tos' | 'privacy' (see legal/LegalModal)

// Design-recreation string not yet in contracts/copyMap (contracts are
// frozen for this surface rebuild — copyMap still carries the pre-redesign
// "CREATE MEMBERSHIP"). Migrate into copyMap when the contract next opens.
const SIGN_UP_CTA_LABEL = 'BEGIN YOUR ARCHIVE';
const CONFIRM_PASSWORD_LABEL = 'CONFIRM PASSWORD';
const CONFIRM_PASSWORD_PLACEHOLDER = 'Confirm your password';

const AuthPanel = ({
  mode = 'sign-in',
  onModeChange,
  onSubmit,
  onGoogle,
  onApple,
  onForgot,
  submitting = false,
  error,
  notice,
  onOpenLegal,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (submitting) return;
    onSubmit && onSubmit(mode, { email, password, confirmPassword });
  };

  // Alert recovery action: 'recover' fires the reset-email flow with whatever
  // email is entered; 'login' flips to the Log In tab.
  const handleAlertAction = (action) => {
    if (!action) return;
    if (action.type === 'recover') onForgot && onForgot(email);
    else if (action.type === 'login') onModeChange && onModeChange('sign-in');
  };

  return (
    <motion.div
      className="bg-[rgba(13,13,12,0.88)] border border-[rgba(255,255,255,0.09)] rounded-card px-6 pt-6 pb-7 sm:px-9 sm:pt-[30px] sm:pb-8"
      data-testid="auth-panel"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* Tabs */}
      <div className="flex border-b border-[rgba(255,255,255,0.08)]" role="tablist">
        {[
          { key: 'sign-in', label: COPY.AUTH_TAB_LOG_IN },
          { key: 'sign-up', label: COPY.AUTH_TAB_SIGN_UP },
        ].map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={mode === tab.key}
            onClick={() => onModeChange && onModeChange(tab.key)}
            className={[
              'flex-1 bg-transparent cursor-pointer pt-3.5 pb-[13px]',
              'border-x-0 border-t-0 border-b-2 border-solid',
              'font-display font-medium transition-colors duration-200',
              mode === tab.key
                ? 'border-aurum text-aurum-bright'
                : 'border-transparent text-bone-dim hover:text-bone',
            ].join(' ')}
            style={{ fontSize: 15, letterSpacing: '0.24em' }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error / notice sit ABOVE the fields so the member sees why the last
          attempt failed before re-entering credentials. Errors use the red
          alert banner (icon + headline + body + optional recovery link);
          success notices stay aurum. */}
      {error && <AuthAlert error={error} onAction={handleAlertAction} disabled={submitting} />}
      {!error && notice && (
        <p
          role="status"
          className="font-display text-aurum text-center mt-[22px] mb-0"
          style={{ fontSize: 14, letterSpacing: '0.04em' }}
        >
          {notice}
        </p>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          id="auth-email"
          className="mt-[26px]"
          label={COPY.SIGN_IN_EMAIL_LABEL}
          icon={<LuMail size={17} />}
          type="email"
          value={email}
          autoComplete="email"
          placeholder={COPY.AUTH_EMAIL_PLACEHOLDER}
          onChange={(e) => setEmail(e.target.value)}
        />

        <Field
          id="auth-password"
          className="mt-5"
          label={COPY.SIGN_IN_PASSWORD_LABEL}
          icon={<LuLock size={17} />}
          type={showPassword ? 'text' : 'password'}
          value={password}
          autoComplete={mode === 'sign-in' ? 'current-password' : 'new-password'}
          placeholder={COPY.AUTH_PASSWORD_PLACEHOLDER}
          onChange={(e) => setPassword(e.target.value)}
          trailing={
            <button
              type="button"
              aria-label={showPassword ? 'hide password' : 'show password'}
              onClick={() => setShowPassword((v) => !v)}
              className="bg-transparent border-0 p-2 cursor-pointer text-bone-dim hover:text-bone"
            >
              {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
            </button>
          }
        />

        {mode === 'sign-up' && (
          <Field
            id="auth-confirm-password"
            className="mt-5"
            label={CONFIRM_PASSWORD_LABEL}
            icon={<LuLock size={17} />}
            type="password"
            value={confirmPassword}
            autoComplete="new-password"
            placeholder={CONFIRM_PASSWORD_PLACEHOLDER}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        )}

        {mode === 'sign-in' && onForgot && (
          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={() => onForgot(email)}
              disabled={submitting}
              className="bg-transparent border-0 p-0 cursor-pointer font-display text-aurum hover:text-aurum-bright disabled:opacity-60 disabled:cursor-default"
              style={{ fontSize: 14, letterSpacing: '0.04em' }}
            >
              {COPY.AUTH_FORGOT_PASSWORD}
            </button>
          </div>
        )}

        {/* Primary CTA */}
        <button
          type="submit"
          disabled={submitting}
          className="w-full h-[54px] mt-6 bg-aurum-faint border border-solid border-aurum rounded-field cursor-pointer flex items-center justify-center gap-3.5 font-display font-medium text-aurum-bright transition-all duration-200 hover:border-aurum-bright hover:shadow-aurum-glow disabled:opacity-60 disabled:cursor-default"
          style={{ fontSize: 15, letterSpacing: '0.24em' }}
        >
          <span>
            {mode === 'sign-in' ? COPY.AUTH_SUBMIT_LOG_IN : SIGN_UP_CTA_LABEL}
          </span>
          <LuArrowRight size={17} className="text-aurum shrink-0" aria-hidden="true" />
        </button>
      </form>

      {(onGoogle || onApple) && (
        <>
          <div className="flex items-center gap-4 mt-6">
            <span className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
            <span
              className="font-display text-bone-dim uppercase"
              style={{ fontSize: 11, letterSpacing: '0.22em' }}
            >
              {COPY.AUTH_OR_CONTINUE_WITH}
            </span>
            <span className="flex-1 h-px bg-[rgba(255,255,255,0.08)]" />
          </div>

          <div className="flex flex-col gap-3 mt-5">
            {onGoogle && (
              <SocialButton onClick={onGoogle} disabled={submitting} icon={<GoogleGlyph />}>
                {COPY.AUTH_GOOGLE_CTA}
              </SocialButton>
            )}
            {onApple && (
              <SocialButton onClick={onApple} disabled={submitting} icon={<AppleGlyph />}>
                {COPY.AUTH_APPLE_CTA}
              </SocialButton>
            )}
          </div>
        </>
      )}

      <p
        className="font-display text-bone-dim text-center mt-[22px] mb-0"
        style={{ fontSize: 14.5, lineHeight: 1.5 }}
      >
        {COPY.AUTH_TERMS_PROSE}{' '}
        <button
          type="button"
          onClick={() => onOpenLegal && onOpenLegal('tos')}
          className="bg-transparent border-0 p-0 cursor-pointer font-display text-aurum hover:text-aurum-bright"
          style={{ fontSize: 'inherit' }}
        >
          {COPY.AUTH_TERMS_LINK_TOS}
        </button>{' '}
        {COPY.AUTH_TERMS_CONJUNCTION}{' '}
        <button
          type="button"
          onClick={() => onOpenLegal && onOpenLegal('privacy')}
          className="bg-transparent border-0 p-0 cursor-pointer font-display text-aurum hover:text-aurum-bright"
          style={{ fontSize: 'inherit' }}
        >
          {COPY.AUTH_TERMS_LINK_PRIVACY}
        </button>
        .
      </p>
    </motion.div>
  );
};

// ── field + button primitives (panel-local) ───────────────────────────────

// Red alert banner shown above the fields on a failed attempt. Red is an
// intentional off-palette exception (DESIGN.md allows only Aurum) reserved for
// error state — kept contained to this one component. Accepts a structured
// { title, detail?, action? } or a bare string (treated as title-only).
const AuthAlert = ({ error, onAction, disabled = false }) => {
  const e = typeof error === 'string' ? { title: error } : error || {};
  return (
    <div
      role="alert"
      className="mt-[26px] flex gap-3.5 rounded-card border border-solid px-4 py-4"
      style={{ borderColor: 'rgba(229,72,77,0.45)', backgroundColor: 'rgba(229,72,77,0.08)' }}
    >
      <LuTriangleAlert
        size={20}
        className="shrink-0 mt-0.5"
        style={{ color: '#E5646A' }}
        aria-hidden="true"
      />
      <div className="min-w-0">
        <p className="font-display font-semibold text-bone m-0" style={{ fontSize: 16, lineHeight: 1.3 }}>
          {e.title}
        </p>
        {e.detail && (
          <p
            className="font-display text-bone-dim mt-2 mb-0"
            style={{ fontSize: 14.5, lineHeight: 1.5 }}
          >
            {e.detail}
          </p>
        )}
        {e.action && (
          <button
            type="button"
            onClick={() => onAction && onAction(e.action)}
            disabled={disabled}
            className="mt-3 bg-transparent border-0 p-0 cursor-pointer font-display underline text-aurum hover:text-aurum-bright disabled:opacity-60 disabled:cursor-default"
            style={{ fontSize: 14.5 }}
          >
            {e.action.label}
          </button>
        )}
      </div>
    </div>
  );
};

const Field = ({ id, className = '', label, icon, trailing, ...inputProps }) => (
  <div className={className}>
    <label
      htmlFor={id}
      className="block font-display uppercase text-bone-dim"
      style={{ fontSize: 11, letterSpacing: '0.2em' }}
    >
      {label}
    </label>
    <div className="relative mt-2">
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-bone-dim pointer-events-none flex items-center">
        {icon}
      </span>
      <input
        id={id}
        {...inputProps}
        className={[
          'w-full h-[52px] bg-[#1B1B1A] border border-solid border-[rgba(255,255,255,0.09)] rounded-field',
          'pl-[46px] font-display text-bone placeholder:text-[#6e6a62]',
          'outline-none transition-all duration-200 focus:border-aurum focus:shadow-aurum-glow',
          trailing ? 'pr-12' : 'pr-4',
        ].join(' ')}
        style={{ fontSize: 17 }}
      />
      {trailing && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
          {trailing}
        </span>
      )}
    </div>
  </div>
);

const SocialButton = ({ onClick, disabled, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="h-[52px] bg-[#1B1B1A] border border-solid border-[rgba(255,255,255,0.09)] rounded-field cursor-pointer flex items-center justify-center gap-3.5 font-display font-medium text-[#E8E2D6] transition-colors duration-200 hover:border-[rgba(255,255,255,0.25)] disabled:opacity-60 disabled:cursor-default"
    style={{ fontSize: 13, letterSpacing: '0.22em' }}
  >
    {icon}
    <span>{children}</span>
  </button>
);

// Brand glyphs are inline SVGs from the design source — the Google "G" must
// keep its real brand colors, and the Apple mark matches the bone palette.
const GoogleGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true" className="shrink-0">
    <path
      fill="#4285F4"
      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
    />
    <path
      fill="#34A853"
      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"
    />
    <path
      fill="#FBBC05"
      d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z"
    />
    <path
      fill="#EA4335"
      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z"
    />
  </svg>
);

const AppleGlyph = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="#E8E2D6" aria-hidden="true" className="shrink-0">
    <path d="M16.7 12.9c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.2-2.9.9-3.7.9-.8 0-1.9-.9-3.2-.9-1.6 0-3.1 1-4 2.4-1.7 3-.4 7.4 1.2 9.8.8 1.2 1.8 2.5 3.1 2.4 1.2 0 1.7-.8 3.2-.8s1.9.8 3.2.8c1.3 0 2.2-1.2 3-2.4.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.6-1-2.7-3.8zM14.4 5.6c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z" />
  </svg>
);

export default AuthPanel;
